import { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import sttService from '../services/sttService';

/**
 * Keeps Speechmatics WebSocket warm while authenticated,
 * closes on logout / long background, and shows a subtle offline indicator
 * only after reconnect attempts are exhausted.
 */
export default function SttWarmupBridge() {
  const { isAuthenticated } = useAuth();
  const [connectionLost, setConnectionLost] = useState(false);

  useEffect(() => {
    return sttService.subscribe?.(({ connectionLost: lost }) => {
      setConnectionLost(Boolean(lost));
    });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      sttService.close?.();
      return undefined;
    }

    sttService.warm?.().catch(() => {});
    return undefined;
  }, [isAuthenticated]);

  useEffect(() => {
    const onVisibility = () => {
      if (!isAuthenticated) return;
      if (document.visibilityState === 'hidden') {
        sttService.handleAppHidden?.();
      } else {
        sttService.handleAppVisible?.();
        sttService.warm?.().catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return () => {
      sttService.close?.();
    };
  }, [isAuthenticated]);

  if (!connectionLost || !isAuthenticated) return null;

  return (
    <div
      className="pointer-events-none fixed right-3 top-3 z-[310] flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-critical shadow-md"
      title="Connection lost"
      aria-label="Connection lost"
    >
      <WifiOff className="h-4 w-4" strokeWidth={2} />
    </div>
  );
}
